import React, {useCallback, useState} from 'react';
import t from 'babel-plugin-yate/macro';
import {Form, Field} from 'react-final-form';
import {useDispatch} from 'react-redux';

import {isRequired} from '../../lib/validations';
import {openBase64File, parseRadioFormField} from '../../lib/misc';
import {getOfferAllDocuments} from '../../store/request';
import RadioButton from '../../components/Form/RadioButton';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import {Text} from '../../components/uiElem';
import Icon from '../../components/Icons';

const fileNameByType = {
  contract_online: t`Vezi contract`,
  draft_insurance: t`Vezi documente asigurare inclusiv costul`,
  privacy_policy_metropolitan_life: t`Vezi politica de prelucrare a datelor personale Metropolitan Life`,
};

export const ModalConfirmSelectedProduct = props => {
  const {
    closeModal,
    onSubmit,
    id,
    uuid,
    isSelectedOfferWithInsurance,
    isSelectedOfferWithCreditCard,
    isSelectedOfferRevolving,
    selectedOfferId,
    selectedOfferDetails,
    addressData,
    requestType,
  } = props;

  const dispatch = useDispatch();
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [documents, setDocuments] = useState([]);

  // const isConsumerFinance = requestType === 4;
  const isPersonalNeeds = requestType === 5;
  const isCreditCard = requestType === 6;

  let titleMain = t`Confirmati semnarea ofertei `;
  let titleByType = t`de bunuri de consum `;
  let titleByOffer = '';
  let titleByIns = t`fara asigurare de viata`;

  if (isPersonalNeeds) {
    titleByType = t`de nevoi personale `;
  }

  if (isCreditCard || isSelectedOfferWithCreditCard) {
    titleByType = 'de credit card ';
  } else if (isSelectedOfferRevolving) {
    titleByOffer = t`revolving `;
  }

  if (isSelectedOfferWithInsurance) {
    titleByIns = t`cu asigurare de viata`;
  }

  const handleGenerateContract = useCallback(async () => {
    setIsGeneratingDocument(true);
    const response = await dispatch(
      getOfferAllDocuments(selectedOfferId, uuid, {
        ...(isSelectedOfferWithCreditCard && addressData),
        ...(selectedOfferDetails && selectedOfferDetails),
      }),
    );
    setIsGeneratingDocument(false);

    if (!response?.ok) {
      return;
    }

    setDocuments(response?.data.data);
  }, [
    selectedOfferId,
    uuid,
    dispatch,
    isSelectedOfferWithCreditCard,
    selectedOfferDetails,
    addressData,
  ]);

  const handleFormSubmit = useCallback(
    formValues => {
      const contract = documents?.filter(
        item => item.type === 'contract_online',
      );

      onSubmit({
        ...formValues,
        offer_id: contract[0].offer_id,
      });
    },
    [documents, onSubmit],
  );

  return (
    <Modal id={id} width="w-full max-w-md" allowClose={false}>
      <Form
        keepDirtyOnReinitialize={true}
        onSubmit={handleFormSubmit}
        render={({handleSubmit}) => (
          <form onSubmit={handleSubmit}>
            <div className="mb-2 p-6">
              <div className="text-22 md:text-30 leading-tight mb-5 text-center text-grey-darkest font-medium">
                {titleMain + titleByType + titleByOffer + titleByIns}
              </div>

              <Text ui="neptune">
                {t`Please make sure that the selected offer is the one for which you want to sign the contract`}
                .
              </Text>

              {!isSelectedOfferWithInsurance && (
                <div className="pt-5">
                  <Text ui="neptune">
                    {t`Nu uitati ca puteti beneficia de protectie prin asigurare in cazul unor evenimente neprevazute
                  daca optati pentru creditul cu asigurare atasata. Pentru a face acest lucru, va rugam sa reluati
                  procesul de alegere a ofertei pentru credit cu asigurare.`}
                  </Text>
                </div>
              )}

              {isSelectedOfferWithInsurance && (
                <>
                  <div className="pt-5">
                    <Field
                      name="insurance_consent"
                      component={RadioButton}
                      type="radio"
                      value={true}
                      validate={isRequired(
                        true,
                        t`Puteti beneficia de avantajele asigurarii doar daca sunteti de acord
                            cu declaratia. Daca nu sunteti de acord cu aceasta, va rugam sa reluati
                            procesul de alegere a ofertei pentru credit fara asigurare.`,
                      )}
                      parse={parseRadioFormField}
                      label={
                        <Text ui="neptune">{t`M-ar ajuta ca Societatea de Asigurari sa plateasca soldul creditului
                          sau rata lunara aferenta creditului, in cazul unui eveniment neprevazut
                          (accident, imbolnavire, somaj).`}</Text>
                      }
                    />
                  </div>

                  <div className="pt-5">
                    <Field
                      name="gdpr_consent_insurance"
                      component={RadioButton}
                      type="radio"
                      value={true}
                      validate={isRequired(true)}
                      parse={parseRadioFormField}
                      label={
                        <Text ui="neptune">{t`Am citit Politica de prelucrare a datelor personale (GDPR)
                        pentru obtinerea asigurarii`}</Text>
                      }
                    />
                  </div>
                </>
              )}

              <div className="pt-5 relative">
                {!!documents.length ? (
                  documents.map(doc => (
                    <div className="flex mb-2" key={doc.type}>
                      <Text ui="venus">
                        <a
                          className="flex items-center"
                          href={openBase64File(doc.file_content)}
                          target="_blank"
                          download={doc.type}
                          rel="noopener noreferrer">
                          <span className="text-red-icon">
                            <Icon name="PDF" />
                          </span>
                          <span className="pl-3">
                            {fileNameByType[doc.type]}
                          </span>
                        </a>
                      </Text>
                    </div>
                  ))
                ) : isGeneratingDocument ? (
                  <div className="h-8 pl-1 flex items-center">
                    <div className="spinner" />
                  </div>
                ) : (
                  <>
                    <Button
                      boxShadow={false}
                      color="blue"
                      border={false}
                      customProps={{type: 'button'}}
                      buttonStyles="py-4 px-8 w-full sm:w-auto sm:min-w-64"
                      title={t`Generate contract`}
                      onClick={handleGenerateContract}
                    />

                    <div className="pt-2">
                      <Text ui="jupiter">
                        {t`Inainte de a semna contractul trebuie generat documentul.`}
                      </Text>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex flex-wrap justify-between p-6 border-t">
              <Button
                color="orange"
                border={false}
                title={t`Incepe semnarea electronica`}
                customProps={{
                  disabled: !documents.length,
                  type: 'submit',
                }}
                buttonStyles="my-1 py-4 px-8 w-full sm:w-auto sm:min-w-64"
              />

              <Button
                boxShadow={false}
                border={true}
                customProps={{type: 'button'}}
                buttonStyles="my-1 py-4 px-8 w-full sm:w-auto sm:min-w-64 sm:ml-8"
                onClick={closeModal}
                title={t`Aleg alta oferta`}
              />
            </div>
          </form>
        )}
      />
    </Modal>
  );
};
